const LIGHTNESS: Record<number, number> = {
  50: 97,
  100: 93,
  200: 86,
  300: 76,
  400: 64,
  500: 54,
  600: 44,
  700: 36,
  800: 28,
  900: 22,
};

const SATURATION_DAMPING: Record<number, number> = {
  50: 0.5,
  100: 0.65,
  200: 0.8,
  300: 0.9,
};

export const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
export type ScaleStep = (typeof SCALE_STEPS)[number];

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Hsl {
  hue: number;
  saturation: number;
  lightness: number;
}

export function parseHex(hex: string): Rgb | null {
  const value = hex.trim().replace(/^#/, '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

function rgbToHsl({ r, g, b }: Rgb): [number, number, number] {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;

  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) return [0, 0, lightness * 100];

  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let hue: number;
  if (max === red) hue = (green - blue) / delta + (green < blue ? 6 : 0);
  else if (max === green) hue = (blue - red) / delta + 2;
  else hue = (red - green) / delta + 4;

  return [hue * 60, saturation * 100, lightness * 100];
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const saturation = s / 100;
  const lightness = l / 100;
  const a = saturation * Math.min(lightness, 1 - lightness);

  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    return Math.round(
      255 * (lightness - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)))),
    );
  };

  return { r: channel(0), g: channel(8), b: channel(4) };
}

export function hexToHsl(hex: string): Hsl | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;

  const [hue, saturation, lightness] = rgbToHsl(rgb);
  return { hue, saturation, lightness };
}

export function hslToChannels(hue: number, saturation: number, lightness: number): string {
  const { r, g, b } = hslToRgb(hue, saturation, lightness);
  return `${r} ${g} ${b}`;
}

export function generateScale(hex: string): Record<ScaleStep, string> | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;

  const [hue, saturation] = rgbToHsl(rgb);

  const scale = {} as Record<ScaleStep, string>;
  for (const step of SCALE_STEPS) {
    scale[step] =
      step === 600
        ? `${rgb.r} ${rgb.g} ${rgb.b}`
        : hslToChannels(hue, saturation * (SATURATION_DAMPING[step] ?? 1), LIGHTNESS[step]);
  }
  return scale;
}

export function toCssColor(channels: string): string {
  return `rgb(${channels})`;
}
